import axios from 'axios';
import { getAuth } from 'firebase/auth';

import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';
import { serverMessageFrom } from 'src/utils/server-error-message';

import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: true
});


axiosInstance.interceptors.request.use(async (config) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => Promise.reject(error));

// Map HTTP status codes to user-friendly error messages. mapHttpError (platform-error-message.js)
// reads these words too: the 408 one keeps "timed out" so it is told as a timeout there.
const FRIENDLY_ERROR_MESSAGES = {
  413: 'Your photos are too large to upload. Remove some or use smaller ones, then try again.',
  408: 'The connection timed out. Check your internet and try again.',
  429: 'Voolist is busy right now. Try again in a minute.',
  502: "Voolist isn't available right now. Try again in a few minutes.",
  503: "Voolist isn't available right now. Try again in a few minutes.",
  504: 'That took too long. Try again.',
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const serverMessage = await serverMessageFrom(error?.response?.data);

    if (serverMessage) {
      error.message = serverMessage;
    } else if (status && FRIENDLY_ERROR_MESSAGES[status]) {
      error.message = FRIENDLY_ERROR_MESSAGES[status];
    } else if (status) {
      // Avoid axios's default "Request failed with status code N" leaking to users
      error.message = 'Something went wrong. Please try again.';
    }

    // Capture every failed request centrally so "it didn't work" is visible in PostHog.
    try {
      const cfg = error.config || {};
      const rawUrl = cfg.url || '';
      let endpoint = String(rawUrl).split('?')[0];
      try { endpoint = new URL(rawUrl, HOST_API || window.location.origin).pathname; } catch (_) { /* keep stripped raw */ }
      trackEvent(ANALYTICS_EVENTS.API_REQUEST_FAILED, {
        endpoint,
        method: (cfg.method || 'get').toUpperCase(),
        status: status || null,
        message: error.message,
        offline: !error.response,
      });
    } catch (_) { /* never break the app from telemetry */ }

    return Promise.reject(error);
  }
);

export default axiosInstance;



// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/',
    login: 'api/user/login',
    register: 'api/user/register',
    ebay: `${HOST_API}/api/auth/ebay/getUrl`,
    etsy: `${HOST_API}/api/auth/etsy`,
    shopify: `${HOST_API}/api/auth/shopify`,
    wooCommerce: `${HOST_API}/api/auth/woocommerce`,
    bigCommerce: `${HOST_API}/api/auth/bigcommerce/install`,
    bigCommerceFinish: `${HOST_API}/api/auth/bigcommerce/finish`,
    revoke: `${HOST_API}/api/auth/revoke`,
    integrations: `${HOST_API}/api/auth/integrations`,
  },
  listing: {
    bulkCreate: `${HOST_API}/api/listings/bulk-create`,
    create: `${HOST_API}/api/listings`,
    list: `${HOST_API}/api/listings`,
    archived: `${HOST_API}/api/listings/archived`,
    details: `${HOST_API}/api/listings/details`,
    imageProxy: `${HOST_API}/api/listings/image-proxy`,
    extensionPublishStatus: `${HOST_API}/api/listings/extension-publish-status`,
    update: `${HOST_API}/api/listings/update`,
    cancelSchedule: (id) => `${HOST_API}/api/listings/${id}/cancel-schedule`,
    reschedule: (id) => `${HOST_API}/api/listings/${id}/reschedule`,
    delete: `${HOST_API}/api/listings/delete`,
    archive: `${HOST_API}/api/listings/archive`,
    archiveStatus: (batchId) => `${HOST_API}/api/listings/archive/status/${batchId}`,
    unmanage: `${HOST_API}/api/listings/unmanage`,
    generate: `${HOST_API}/api/listings/generate`,
    markSold: `${HOST_API}/api/listings/mark-sold`,
    delist: `${HOST_API}/api/listings/delist`,
    delistStatus: (batchId) => `${HOST_API}/api/listings/delist/status/${batchId}`,
    delistRelist: `${HOST_API}/api/listings/delist-relist`,
    importSelected: `${HOST_API}/api/listings/import-selected`,
    // Merge endpoints
    mergePreview: `${HOST_API}/api/listings/merge/preview`,
    detectDuplicates: `${HOST_API}/api/listings/detect-duplicates`,
    mergeImported: `${HOST_API}/api/listings/merge-imported`,
    // Bulk edit endpoints
    bulkEditExecute: `${HOST_API}/api/listings/bulk-edit/execute`,
    bulkEditStatus: (batchId) => `${HOST_API}/api/listings/bulk-edit/status/${batchId}`,
    // Bulk merge endpoints
    bulkMergeDetectGroups: `${HOST_API}/api/listings/bulk-merge/detect-groups`,
    bulkMergeExecute: `${HOST_API}/api/listings/bulk-merge/execute`,
    bulkMergeStatus: (batchId) => `${HOST_API}/api/listings/bulk-merge/status/${batchId}`,
    // CSV import endpoints
    csvImport: `${HOST_API}/api/listings/csv-import`,
    csvTemplate: `${HOST_API}/api/listings/csv-template`,
    // Inkfrog import endpoints
    inkfrogImport: `${HOST_API}/api/listings/inkfrog-import`,
    inkfrogImportStatus: (jobId) => `${HOST_API}/api/listings/inkfrog-import/${jobId}/status`,
    inkfrogImportConfirm: (jobId) => `${HOST_API}/api/listings/inkfrog-import/${jobId}/confirm`,
    uploadDescriptionImage: `${HOST_API}/api/listings/upload-description-image`,
    // Pending platform syncs (extension platforms)
    pendingPlatformSyncs: `${HOST_API}/api/extension/pending-platform-syncs`,
    // Record that a new Poshmark/Depop post is owed on one account (Delist & Relist, after the old post is gone)
    platformSyncQueue: (listingId) => `${HOST_API}/api/extension/platform-sync-queue/${listingId}`,
    // Pull-from-platform — per-account sync of LPA fields from the live platform listing
    pullFromPlatform: (listingId, integrationId) => `${HOST_API}/api/listings/${listingId}/pull-from-platform/${integrationId}`,
    applyExtensionFetch: (listingId, integrationId) => `${HOST_API}/api/listings/${listingId}/platform-accounts/${integrationId}/apply-fetch`,
    deactivatePlatformAccount: (listingId, integrationId) => `${HOST_API}/api/listings/${listingId}/platform-accounts/${integrationId}/deactivate`,
  },
  management: {
    ebay: {
      category: `${HOST_API}/api/management/category/ebay`,
      // The seller's own eBay Store categories (per account; needs a store subscription)
      storeCategories: `${HOST_API}/api/management/category/ebay/store`,
      categoryFields: `${HOST_API}/api/management/category/ebay/categoryFields`,
      // eBay Motors (Parts & Accessories) parts-compatibility / fitment (category tree 100)
      compatibilityProperties: `${HOST_API}/api/management/category/ebay/compatibilityProperties`,
      compatibilityValues: `${HOST_API}/api/management/category/ebay/compatibilityValues`,
      policies: {
        fetch: `${HOST_API}/api/management/policies/ebay/fetch`,
        get: `${HOST_API}/api/management/policies/ebay/get`,
        root: `${HOST_API}/api/management/policies/ebay`,
      },
      location: `${HOST_API}/api/management/ebay/location`,
      policy: `${HOST_API}/api/management/ebay/policy`,
      itemCondition: `${HOST_API}/api/management/ebay/itemCondition`,
      details: `${HOST_API}/api/management/ebay/details`,
      shippingServices: `${HOST_API}/api/management/ebay/shipping-services`,
      postalCode: `${HOST_API}/api/management/ebay/postal-code`,
    },
    etsy: {
      category: `${HOST_API}/api/management/category/etsy`,
      categoryFields: `${HOST_API}/api/management/category/etsy/categoryFields`,
      // The groupings the seller made inside their own Etsy shop (per account)
      shopSections: `${HOST_API}/api/management/category/etsy/sections`,
      returnPolicy: `${HOST_API}/api/management/return-policy/etsy`,
      shippingProfile: `${HOST_API}/api/management/shipping-profile/etsy`,
      policies: {
        fetch: `${HOST_API}/api/management/policies/etsy/fetch`,
        get: `${HOST_API}/api/management/policies/etsy/get`,
        root: `${HOST_API}/api/management/policies/etsy`,
      },
    },
    // The categories the seller made in their own WooCommerce or BigCommerce store (per account)
    productCategories: `${HOST_API}/api/management/product-categories`,
    marketplaces: `${HOST_API}/api/management/marketplaces`,
    settings: `${HOST_API}/api/management/settings`,
    brands: { search: `${HOST_API}/api/management/brands/search` },
    depop: {
      attributes: `${HOST_API}/api/management/depop/attributes`,
      categoryFilters: `${HOST_API}/api/management/depop/category-filters`,
    },
    poshmark: { catalog: `${HOST_API}/api/management/poshmark/catalog` },
  },
  notification: `${HOST_API}/api/notifications`,
  user: `${HOST_API}/api/user`,
  onboarding: `${HOST_API}/api/user/onboarding`,
  usage: `${HOST_API}/api/user/usage`,
  admin: {
    dashboard: `${HOST_API}/api/admin/dashboard`,
    users: `${HOST_API}/api/admin/stats/users`,
    listings: `${HOST_API}/api/admin/stats/listings`,
    integrations: `${HOST_API}/api/admin/stats/integrations`,
    trends: `${HOST_API}/api/admin/stats/trends`,
    recentUsers: `${HOST_API}/api/admin/users/recent`,
    redis: `${HOST_API}/api/admin/stats/redis`,
    workerHealth: `${HOST_API}/api/admin/stats/worker-health`,
    syncTracker: `${HOST_API}/api/admin/stats/sync-tracker`,
    refundEligibility: (userId) => `${HOST_API}/api/admin/users/${userId}/refund-eligibility`,
    refundAndDisable: (userId) => `${HOST_API}/api/admin/users/${userId}/refund-and-disable`,
    enterprisePlans: `${HOST_API}/api/admin/enterprise-plans`,
    enterprisePlan: (key) => `${HOST_API}/api/admin/enterprise-plans/${key}`,
  },
  template: {
    list: `${HOST_API}/api/templates`,
    public: `${HOST_API}/api/templates/public`,
    details: (id) => `${HOST_API}/api/templates/${id}`,
    apply: (id) => `${HOST_API}/api/templates/${id}/apply`,
    duplicate: (id) => `${HOST_API}/api/templates/${id}/duplicate`,
  },
  bulkCrossList: {
    analyze: `${HOST_API}/api/bulk-cross-list/analyze`,
    execute: `${HOST_API}/api/bulk-cross-list/execute`,
    status: (batchId) => `${HOST_API}/api/bulk-cross-list/status/${batchId}`,
  },

};
